import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../ui/card';
import { Button } from '../../../ui/button';
import {
  Trophy,
  Users,
  Building,
  Globe,
  TrendingUp,
  FileText,
  Eye,
  Edit3,
  BookOpen
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

// Top Agents Panel
const TopAgentsPanel = ({ agents, loading }) => {
  const navigate = useNavigate();

  if (loading || !agents) {
    return <PanelSkeleton title="Top Agents" icon={Trophy} />;
  }

  return (
    <Card className="p-5 bg-white border-stone-200/80">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-amber-50 border border-amber-100">
            <Trophy className="w-4 h-4 text-amber-600" />
          </div>
          <h3 className="font-semibold text-gray-900 font-outfit">Top Agents</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/agents')} className="text-gray-500 hover:text-[#F2575F]">
          View All
        </Button>
      </div>
      <div className="space-y-1">
        {agents.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No agent data yet</p>
        ) : (
          agents.map((agent, index) => (
            <div
              key={agent.userId}
              className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-stone-50 transition-colors cursor-pointer"
              onClick={() => navigate(`/admin/agents/${agent.userId}`)}
            >
              <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                index === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm' :
                index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' :
                index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white' : 'bg-stone-100 text-gray-600'
              }`}>
                {index + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{agent.name}</p>
                <p className="text-xs text-gray-500">{agent.applications} students</p>
              </div>
              <span className="text-sm font-bold text-emerald-600">
                ${agent.revenue?.toLocaleString() || 0}
              </span>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Counselor Workload Panel
const CounselorWorkloadPanel = ({ counselors, loading }) => {
  const navigate = useNavigate();

  if (loading || !counselors) {
    return <PanelSkeleton title="Counselor Workload" icon={Users} />;
  }

  const maxWorkload = Math.max(...counselors.map(c => c.activeRequests), 1);

  return (
    <Card className="p-5 bg-white border-stone-200/80">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-purple-50 border border-purple-100">
            <Users className="w-4 h-4 text-purple-600" />
          </div>
          <h3 className="font-semibold text-gray-900 font-outfit">Counselor Workload</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/counselors')} className="text-gray-500 hover:text-[#F2575F]">
          View All
        </Button>
      </div>
      <div className="space-y-3">
        {counselors.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No counselor data yet</p>
        ) : (
          counselors.map((counselor) => (
            <div
              key={counselor.userId}
              className="space-y-1.5 p-2 rounded-lg hover:bg-stone-50 transition-colors cursor-pointer"
              onClick={() => navigate(`/admin/counselors/${counselor.userId}`)}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">{counselor.name}</span>
                <span className="text-xs font-semibold text-gray-600">{counselor.activeRequests} active</span>
              </div>
              <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    counselor.activeRequests > 20 ? 'bg-gradient-to-r from-red-400 to-red-500' :
                    counselor.activeRequests > 15 ? 'bg-gradient-to-r from-amber-400 to-amber-500' : 'bg-gradient-to-r from-[#F2575F] to-[#E04850]'
                  }`}
                  style={{ width: `${(counselor.activeRequests / maxWorkload) * 100}%` }}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Top Universities Panel
const TopUniversitiesPanel = ({ universities, loading }) => {
  const navigate = useNavigate();

  if (loading || !universities) {
    return <PanelSkeleton title="Top Universities" icon={Building} />;
  }

  return (
    <Card className="p-5 bg-white border-stone-200/80">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-50 border border-blue-100">
            <Building className="w-4 h-4 text-blue-600" />
          </div>
          <h3 className="font-semibold text-gray-900 font-outfit">Top Universities</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/universities')} className="text-gray-500 hover:text-[#F2575F]">
          View All
        </Button>
      </div>
      <div className="space-y-1">
        {universities.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No university data yet</p>
        ) : (
          universities.map((uni, index) => (
            <div
              key={uni.universityId || index}
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-stone-50 transition-colors"
            >
              <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 border border-blue-100">
                {uni.imageUrl ? (
                  <img src={uni.imageUrl} alt="" className="w-6 h-6 object-contain" />
                ) : (
                  <Building className="w-4 h-4 text-blue-500" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{uni.name}</p>
                <p className="text-xs text-gray-500">{uni.country}</p>
              </div>
              <span className="text-sm font-bold text-gray-700">
                {uni.applications}
              </span>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Top Countries Panel
const TopCountriesPanel = ({ countries, loading }) => {
  if (loading || !countries) {
    return <PanelSkeleton title="Student Origins" icon={Globe} />;
  }

  return (
    <Card className="p-5 bg-white border-stone-200/80">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-teal-50 border border-teal-100">
            <Globe className="w-4 h-4 text-teal-600" />
          </div>
          <h3 className="font-semibold text-gray-900 font-outfit">Student Origins</h3>
        </div>
      </div>
      <div className="space-y-1">
        {countries.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No country data yet</p>
        ) : (
          countries.map((country, index) => (
            <div
              key={country.name || index}
              className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-stone-50 transition-colors"
            >
              <span className="text-2xl">{country.flag}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{country.name}</p>
              </div>
              <span className="text-sm font-bold text-gray-700">
                {country.students}
              </span>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Content Metrics Panel (Blog)
const ContentMetricsPanel = ({ contentMetrics, loading }) => {
  const navigate = useNavigate();

  if (loading || !contentMetrics) {
    return <PanelSkeleton title="Content Metrics" icon={FileText} />;
  }

  return (
    <Card className="p-5 bg-white border-stone-200/80">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-rose-50 border border-rose-100">
            <BookOpen className="w-4 h-4 text-rose-600" />
          </div>
          <h3 className="font-semibold text-gray-900 font-outfit">Content Metrics</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/blogs')} className="text-gray-500 hover:text-[#F2575F]">
          Manage
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="p-3 rounded-xl bg-blue-50/80 border border-blue-100">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-medium text-blue-600">Total</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{contentMetrics.totalBlogs || 0}</p>
        </div>
        <div className="p-3 rounded-xl bg-emerald-50/80 border border-emerald-100">
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-xs font-medium text-emerald-600">Views</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{contentMetrics.totalViews?.toLocaleString() || 0}</p>
        </div>
      </div>

      {/* Status breakdown */}
      <div className="flex items-center gap-3 mb-4 p-2 rounded-lg bg-stone-50 border border-stone-100">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 bg-emerald-500 rounded-full" />
          <span className="text-xs text-gray-600">{contentMetrics.publishedBlogs || 0} Published</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 bg-amber-500 rounded-full" />
          <span className="text-xs text-gray-600">{contentMetrics.draftBlogs || 0} Drafts</span>
        </div>
      </div>

      {/* Recent blogs */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Recent Posts</p>
        {(!contentMetrics.recentBlogs || contentMetrics.recentBlogs.length === 0) ? (
          <div className="flex flex-col items-center py-4 text-center">
            <Edit3 className="w-8 h-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No blog posts yet</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 border-stone-200"
              onClick={() => navigate('/admin/blogs/new')}
            >
              Create First Post
            </Button>
          </div>
        ) : (
          contentMetrics.recentBlogs.slice(0, 3).map((blog, index) => (
            <div
              key={blog.slug || index}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-stone-50 transition-colors cursor-pointer"
              onClick={() => navigate(`/admin/blogs/${blog.slug}`)}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{blog.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                    blog.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {blog.status}
                  </span>
                  {blog.views > 0 && (
                    <span className="text-xs text-gray-400">{blog.views} views</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

// Trends Chart Panel
const TrendsPanel = ({ trends, loading }) => {
  if (loading || !trends) {
    return <PanelSkeleton title="Trends" icon={TrendingUp} />;
  }

  const chartData = trends.students?.map((item, index) => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    students: item.value,
    applications: trends.applications?.[index]?.value || 0
  })) || [];

  return (
    <Card className="p-5 bg-white border-stone-200/80">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-indigo-50 border border-indigo-100">
            <TrendingUp className="w-4 h-4 text-indigo-600" />
          </div>
          <h3 className="font-semibold text-gray-900 font-outfit">30-Day Trends</h3>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-[#F2575F] rounded-full" />
            Students
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-indigo-500 rounded-full" />
            Apps
          </span>
        </div>
      </div>
      <div className="h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="studentGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F2575F" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#F2575F" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="appGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366F1" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e7e5e4',
                borderRadius: '10px',
                fontSize: '12px',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
              }}
            />
            <Area
              type="monotone"
              dataKey="students"
              stroke="#F2575F"
              fill="url(#studentGrad)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="applications"
              stroke="#6366F1"
              fill="url(#appGrad)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

// Panel Skeleton
const PanelSkeleton = ({ title, icon: Icon }) => (
  <Card className="p-5 bg-white border-stone-200/80">
    <div className="flex items-center gap-2 mb-4">
      <div className="p-1.5 rounded-lg bg-stone-50 border border-stone-100">
        <Icon className="w-4 h-4 text-gray-400" />
      </div>
      <h3 className="font-semibold text-gray-900 font-outfit">{title}</h3>
    </div>
    <div className="space-y-3">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-12 bg-stone-50 rounded-xl animate-pulse" />
      ))}
    </div>
  </Card>
);

// Main Component
export function OperationalPanels({
  topAgents,
  counselorWorkload,
  topUniversities,
  topCountries,
  trends,
  contentMetrics,
  loading
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
    >
      <TopAgentsPanel agents={topAgents} loading={loading} />
      <CounselorWorkloadPanel counselors={counselorWorkload} loading={loading} />
      <TrendsPanel trends={trends} loading={loading} />
      <TopUniversitiesPanel universities={topUniversities} loading={loading} />
      <TopCountriesPanel countries={topCountries} loading={loading} />
      <ContentMetricsPanel contentMetrics={contentMetrics} loading={loading} />
    </motion.div>
  );
}

export default OperationalPanels;
